/**
 * The reveal cadence table and its arithmetic — framework-free (D137,
 * Calder's 2.4).
 *
 * @remarks
 * Cadence is characterization (D99): the player and a handwritten log are
 * INSTANT (their text was produced before the reader arrived — one typed it a
 * moment ago, the other wrote it nineteen years ago and is being READ, not
 * produced); the facility's hardware steps evenly (the one channel where the
 * effect is not a metaphor); the environment comes into focus, because nobody
 * is typing a room into existence. A SPEAKER's cadence is the pack's to
 * declare (D192) — the POC's narrator steps at 14 ms, metronomic, because the
 * absence of hesitation IS her characterization (D99), and that is what a
 * speaker who declares nothing gets.
 *
 * This module imports no React so that a page with none — the 404 (Sector
 * Null) — can read the same table the transcript reads, through the
 * `@orpheus/player-ui/reveal` subpath. Before D137 the 404 carried a
 * hand-copied table, declared as duplication at the site; a table whose
 * whole point is that cadence is characterization cannot have two copies
 * that agree by hand. D102 moved the paces out of the stylesheet for the
 * same reason: a turn's lines animate in ORDER, ordering needs durations,
 * durations need the paces — one table, one source, handed to the CSS as
 * variables.
 *
 * @packageDocumentation
 */

import { DEFAULT_SPEAKER_REVEAL, type FixedTranscriptSource, isFixedTranscriptSource, type TranscriptSource } from '@orpheus/engine-runtime';

import { revealStepsOf } from './redaction.js';

// D145: the redaction seam rides the same framework-free subpath, because
// the plain-DOM renderer that reads this table must step the same units.
export {
	REDACTION_TOKEN,
	type RedactionSegment,
	type RevealStep,
	revealStepsOf,
	segmentRedactions,
} from './redaction.js';

/** How one line's text arrives on screen. */
export type RevealCadence =
	/** A soft coming-into-focus — perception, not keystrokes (environment). */
	| 'focus'
	/** No animation at all — the text was never "produced" for the reader. */
	| 'instant'
	/** Character-by-character, evenly — a machine putting text on a display. */
	| 'stepped';

/** One channel's cadence and, for the stepped cadence, its pace. */
export interface RevealCadenceDeclaration {
	readonly cadence: RevealCadence;
	readonly stepMs?: number;
}

/**
 * R27 — each fixed channel's cadence, from Calder's table and Mike's
 * metronome ruling (D99). A declared speaker's row is the pack's own
 * (`speakers[].reveal`, D192), resolved by {@link revealDeclarationOf}.
 */
export const REVEAL_CADENCE_BY_SOURCE: Readonly<Record<FixedTranscriptSource, RevealCadenceDeclaration>> = {
	environment: { cadence: 'focus' },
	human_log: { cadence: 'instant' },
	player: { cadence: 'instant' },
	// A teletype has moving parts — slower than a speaker, and the one
	// channel where the effect is not a metaphor.
	system: { cadence: 'stepped', stepMs: 24 },
};

/**
 * A declared speaker, as the cadence reader needs it: the id a line's
 * `source` carries and the reveal the pack gave it (already resolved — a
 * pack that said nothing gets the metronome, D99, and the host says so).
 */
export interface SpeakerReveal {
	readonly reveal: RevealCadenceDeclaration;
	readonly speakerId: string;
}

/** The four fixed styling modifiers, plus the one every declared speaker shares. */
export type SourceModifier = FixedTranscriptSource | 'speaker';

/**
 * The styling modifier a line's `source` becomes (D192): one of the engine's
 * four fixed sources by name, or `speaker` for any declared voice — so a
 * stylesheet names the engine's own channels and never a pack's character.
 * Framework-free, beside the cadence, because the 404 page draws lines with
 * no React and needs both.
 *
 * @param source - The line's channel
 * @returns The BEM modifier
 */
export const sourceModifierOf = (source: TranscriptSource): SourceModifier => (isFixedTranscriptSource(source) ? source : 'speaker');

/**
 * The cadence one line gets: the fixed table for the engine's own channels,
 * the speaker's declaration for a declared voice, and the metronome (D99)
 * for a voice nothing declared — a save carrying a speaker the pack has
 * since dropped, or a surface with no pack at all.
 *
 * @param source - The line's channel
 * @param speakers - The pack's declared speakers with their reveals
 * @returns The declaration to render by
 */
export const revealDeclarationOf = (source: TranscriptSource, speakers: readonly SpeakerReveal[]): RevealCadenceDeclaration =>
	isFixedTranscriptSource(source)
		? REVEAL_CADENCE_BY_SOURCE[source]
		: (speakers.find((speaker) => speaker.speakerId === source)?.reveal ?? DEFAULT_SPEAKER_REVEAL);

/**
 * Past this length a stepped reveal is a PRINTOUT rather than a line being
 * typed — a multi-second wait and thousands of spans (the ASCII map is the
 * shipped case). Past it the stepped cadence degrades to the focus reveal:
 * same completeness, one element.
 */
export const STEPPED_REVEAL_LENGTH_LIMIT = 400;

/** The focus reveal's duration — perception arriving, not keystrokes. */
export const FOCUS_REVEAL_DURATION_MS = 600;

/** The default per-character pace when a caller declares none. */
export const DEFAULT_STEP_MS = 16;

/**
 * The reveal a line actually gets, once the length limit has had its say —
 * the one decision both renderers (React and plain DOM) used to make each
 * for themselves.
 */
export type ResolvedReveal =
	| { readonly kind: 'focus' }
	| { readonly kind: 'instant' }
	| { readonly kind: 'stepped'; readonly stepMs: number };

/**
 * Resolve a declared cadence against the line it will reveal.
 *
 * @param cadence - The line's declared reveal
 * @param text - The full line
 * @param stepMs - Per-character pace for the stepped cadence
 * @returns What the renderer should draw
 */
export const resolveReveal = (cadence: RevealCadence, text: string, stepMs: number = DEFAULT_STEP_MS): ResolvedReveal => {
	if (cadence === 'instant') {
		return { kind: 'instant' };
	}

	if (cadence === 'focus' || text.length > STEPPED_REVEAL_LENGTH_LIMIT) {
		return { kind: 'focus' };
	}

	return { kind: 'stepped', stepMs };
};

/**
 * How long one line's reveal takes — the sequencing currency (D102): a
 * turn's lines animate in order, so each line's start is the sum of its
 * predecessors' durations, and this is the one place that sum's terms come
 * from.
 *
 * @param cadence - The line's reveal
 * @param text - The full line
 * @param stepMs - Per-character pace for the stepped cadence
 * @returns The reveal's duration in milliseconds
 */
export const revealDurationOf = (cadence: RevealCadence, text: string, stepMs: number = DEFAULT_STEP_MS): number => {
	const reveal = resolveReveal(cadence, text, stepMs);

	switch (reveal.kind) {
		case 'focus': {
			return FOCUS_REVEAL_DURATION_MS;
		}
		case 'instant': {
			return 0;
		}
		case 'stepped': {
			// One step per code point and one per redaction (D145) — the same
			// units every renderer draws, so the sequencing sum cannot drift
			// from the reveal it times.
			return revealStepsOf(text).length * reveal.stepMs;
		}
	}
};

/**
 * How long a transcript entry's reveal takes when it arrives, by its
 * channel's declared cadence.
 *
 * @param entry - The entry's channel and text
 * @param speakers - The pack's declared speakers with their reveals
 * @returns The reveal duration in milliseconds
 */
export const entryRevealDurationOf = (entry: { readonly source: TranscriptSource; readonly text: string }, speakers: readonly SpeakerReveal[]): number => {
	const { cadence, stepMs } = revealDeclarationOf(entry.source, speakers);

	return revealDurationOf(cadence, entry.text, stepMs);
};
